import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  Plugin,
  LegendItem,
  ChartEvent,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { ToggleChips } from '../../Dashboard/dashboard-components/ToggleChips';
import { valChips } from '../../Dashboard/dashboardUtils';
import { shorten, TimeGranularity } from './StrategiesUtils';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { renderCoverageTooltipHTML } from './coverageTooltipUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

export interface DatasetConfig {
  label: string;
  dataKey: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  borderDash?: number[];
  type?: 'bar' | 'line';
  hidden?: boolean;
  fill?: boolean;
  pointRadius?: number;
  hideFromLegend?: boolean;
}

interface Props<T> {
  title: string;
  data: T[];
  matPrice: number;
  unit: IMassUnitObject;
  onTimeChange: (_time: TimeGranularity) => void;
  datasets: DatasetConfig[];
  pluginsFactory?: (_isQuantity: boolean, _matPrice: number) => Plugin[];
  stacked?: boolean;
  extraParams?: any;
  showPercentageLabels?: boolean;
  materialId: string;
  chartKey?: string;
}

export function StrategyChart<T extends { month: string; [key: string]: any }>({
  title,
  data,
  matPrice,
  unit,
  onTimeChange,
  datasets,
  pluginsFactory,
  stacked = false,
  extraParams,
  showPercentageLabels = false,
  materialId,
  chartKey: externalChartKey,
}: Props<T>) {
  const [isQuantity, setIsQuantity] = useState<boolean>(true);
  const [timeGrans, setTimeGrans] = useState<number>(0);
  const [viewMonths, setViewMonths] = useState<number>(12);
  const [visibleDsIdx, setVisibleDsIdx] = useState<number>(2); // For buffer scenario
  const [isFirstHidden, setIsFirstHidden] = useState<boolean>(false); // For logistics/coverage scenario

  const timeGranularityMap: Record<number, TimeGranularity> = {
    0: 'Month',
    1: 'Quarter',
    2: 'Terminal',
  };

  React.useEffect(() => {
    onTimeChange(timeGranularityMap[timeGrans]);
  }, [timeGrans, onTimeChange]);

  const displayData = data.slice(0, viewMonths); //, [data, viewMonths, materialId]);

  const timeOpts = [
    { key: 'Month', value: 0 },
    { key: 'Quarter', value: 1, disabled: true },
    { key: 'Terminal', value: 2, disabled: true },
  ];
  const viewRangeOpts = [
    { key: '1 Year', value: 12 },
    { key: '2 Years', value: 24 },
  ];

  const basePlugins = pluginsFactory ? pluginsFactory(isQuantity, matPrice) : [];

  // Add percentage label plugin if needed
  const plugins = showPercentageLabels
    ? [
        ...basePlugins,
        {
          id: 'percentageLabels',
          afterDatasetsDraw(chart) {
            const { ctx, data, scales } = chart;
            const xScale = scales.x;
            const yScale = scales.y;

            // Find the inventory/coverage bar dataset (first dataset)
            const inventoryDataset = data.datasets[1];
            const shortage = data.datasets[2];
            // Find the expected demand dataset (dashed line)
            const demandDataset = data.datasets[0]; //.find((ds) => ds.label?.includes('Expected'));
            const amountNeeded = data.datasets[3];

            if (!inventoryDataset || !demandDataset) return;

            ctx.save();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.font = '12px sans-serif';

            inventoryDataset.data.forEach((value: any, index: number) => {
              const inventoryValue = value || 0;
              const demandValue = ((demandDataset.data[index] || shortage.data[index]) as number) || 0;
              const requiredAmount = (amountNeeded.data[index] as number) || 0;
              const originalRow = displayData[index];

              const percentage = originalRow['covPercentage'] || 0;
              const x = xScale.getPixelForValue(index);
              const y = yScale.getPixelForValue(demandValue);

              // Draw percentage label above the bar
              ctx.fillStyle = '#374151';
              ctx.fillText(`${percentage}%`, x, y - 5);
            });

            ctx.restore();
          },
        } as Plugin,
      ]
    : basePlugins;

  const chartData = useMemo(() => {
    const labels = displayData.map((item) => item.month);
    const multiplier = isQuantity ? 1 : matPrice;

    return {
      labels,
      datasets: datasets.map((dataset, dsIdx) => {
        // Calculate the actual data based on current visibility state
        let dataKey = dataset.dataKey;

        // For logistics/coverage scenario: adjust second dataset's dataKey based on first dataset visibility
        if ((extraParams?.logistics || extraParams?.isCoverage) && dsIdx === 1) {
          dataKey = isFirstHidden
            ? 'expectedDemand'
            : extraParams?.isCoverage
            ? 'visCovExpectDemand'
            : 'visExpectDemand';
        }

        const rawData = displayData.map((item) => {
          const value = item[dataKey];
          return value !== null && value !== undefined ? value * multiplier : null;
        });

        // For buffer scenario: determine visibility based on visibleDsIdx
        let isHidden = dataset.hidden;
        let hideFromLegend = dataset.hideFromLegend;

        if (extraParams?.buffer && dsIdx > 1 && dsIdx < 6) {
          isHidden = dsIdx !== visibleDsIdx;
          hideFromLegend = dsIdx !== visibleDsIdx;
        }

        return {
          label: dataset.label,
          data: rawData,
          backgroundColor: dataset.backgroundColor || '#93C5FD',
          borderColor: dataset.borderColor || dataset.backgroundColor || '#3B82F6',
          borderWidth: dataset.borderWidth ?? (dataset.type === 'line' ? 2 : 0),
          borderRadius: dataset.borderRadius ?? 0,
          borderSkipped: false,
          borderDash: dataset.borderDash,
          type: dataset.type || 'bar',
          hidden: isHidden,
          hideFromLegend: hideFromLegend, // This controls legend visibility
          fill: dataset.fill ?? false,
          pointRadius: dataset.pointRadius ?? (dataset.type === 'line' ? 0 : undefined),
        };
      }),
    };
  }, [
    displayData,
    isQuantity,
    matPrice,
    datasets,
    extraParams,
    visibleDsIdx,
    isFirstHidden,
    materialId,
    extraParams?.isCoverage,
  ]);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    barPercentage: 0.5,
    categoryPercentage: 0.8,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        onClick: (_event: ChartEvent, legendItem: LegendItem, legend: Legend<'bar'>) => {
          const datasetIndex = legendItem.datasetIndex;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(datasetIndex);

          if (extraParams?.logistics || extraParams?.isCoverage) {
            meta.hidden = !meta.hidden;

            // Update the legend item's hidden state
            legendItem.hidden = !legendItem.hidden;

            // If toggling the first dataset, update the second dataset's data
            if (datasetIndex === 0) {
              const newIsFirstHidden = meta.hidden;
              setIsFirstHidden(newIsFirstHidden);

              // Determine the new dataKey
              const newDataKey = newIsFirstHidden
                ? 'expectedDemand'
                : extraParams?.isCoverage
                ? 'visCovExpectDemand'
                : 'visExpectDemand';

              // Update the second dataset's data
              const multiplier = isQuantity ? 1 : matPrice;
              const newData = displayData.map((item) => {
                const value = item[newDataKey];
                return value !== null && value !== undefined ? value * multiplier : null;
              });

              chart.data.datasets[1].data = newData;
            }
            // Update the chart
            chart.update();
          } else if (extraParams?.buffer && datasetIndex !== undefined && [0, 1].includes(datasetIndex)) {
            meta.hidden = !meta.hidden;
            // Update the legend item's hidden state
            legendItem.hidden = !legendItem.hidden;
            const stockHidden = chart.getDatasetMeta(0).hidden;
            const poHidden = chart.getDatasetMeta(1).hidden;
            //const dataSets = ['visibleExpectedDemand'2, 'expectedDemand'3, 'visExpectedMinusPO'4, 'visExpectedMinusStock'5];
            const newVisibleDsIdx = stockHidden ? (poHidden ? 3 : 4) : poHidden ? 5 : 2;
            setVisibleDsIdx(newVisibleDsIdx);

            chart.data.datasets.forEach((dataset: any, dsIdx: number) => {
              if (dsIdx > 1 && dsIdx < 6) {
                dataset.hidden = dsIdx !== newVisibleDsIdx;
                dataset.hideFromLegend = dsIdx !== newVisibleDsIdx;
              }
            });

            // Update the chart
            chart.update();
          }
        },
        labels: {
          padding: 20,
          font: {
            size: 12,
          },
          filter: (item, data) => {
            // Hide dataset from legend if hideFromLegend is true
            const dataset = data.datasets[item?.datasetIndex || 0] as any;
            return !(dataset as any & { hideFromLegend: boolean }).hideFromLegend;
          },
        },
      },
      tooltip: {
        enabled: false,
        external: (context) => {
          // Tooltip Element
          let tooltipEl = document.getElementById('chartjs-tooltip');

          // Create element on first render
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.style.background = 'white';
            tooltipEl.style.borderRadius = '8px';
            tooltipEl.style.border = '1px solid #ccc';

            tooltipEl.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            tooltipEl.style.opacity = '1';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.transform = 'translate(-50%, 0)';
            tooltipEl.style.transition = 'all .1s ease';
            tooltipEl.style.padding = '12px';
            tooltipEl.style.minWidth = '200px';
            document.body.appendChild(tooltipEl);
          }

          // Hide if no tooltip
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          // Set Text
          if (tooltipModel.body) {
            const dataPoints = tooltipModel.dataPoints;

            const indexes = dataPoints.map((dp) => ({
              datasetIndex: dp.datasetIndex,
              dataIndex: dp.dataIndex,
            }));

            // Example: get original value from your props.data + datasets config
            // assuming first dataPoint is enough
            const { dataIndex } = indexes[0];
            const originalRow = displayData[dataIndex]; //;|| data[dataIndex]; // if not sliced
            //console.log('orig', displayData, dataIndex);
            const labels = tooltipModel.dataPoints.map((dp) => dp.dataset.label || '');
            const values = tooltipModel.dataPoints.map((dp) => dp.raw?.toString() || '');
            const colors = extraParams?.buffer
              ? ['#1E3A8A', '#3B82F6', '#9f9f9f', 'red']
              : ['#3B82F6', '#9f9f9f', 'red', 'white'];

            if (extraParams && extraParams.logistics) {
              const {
                inventory,
                expectedDemand,
                futureOrders,
                requiredQuantity,
                stock,
                logisticAmnt,
                emergencyStock,
                POs,
                currCover,
                stockNoRatio,
                sumPoNoRatio,
                financialPositions,
                covRatio,
                reqCover,
                covPercentage,
              } = originalRow;

              if (extraParams.isCoverage) {
                labels[0] = 'Current coverage (storage + POs + financial positions)';
                values[0] = `${currCover} (${stockNoRatio} + ${sumPoNoRatio} + ${financialPositions})`;
                labels[1] = 'Expected demand (MRP + Buffer)';
                values[1] = `${expectedDemand} (${futureOrders} + ${emergencyStock})`;
                labels[2] = 'Required coverage (expected demand * strategy)';
                values[2] = `${reqCover} (${expectedDemand} * ${covRatio}%)`;
                labels[3] = 'Coverage % (Current coverage / required coverage)';
                values[3] = `${covPercentage}% (${currCover} / ${reqCover})`;
              } else {
                // Logistics
                labels[0] = 'Inventory (storage + POs - buffer)';
                values[0] = `${inventory} (${stock} + ${POs} - ${emergencyStock})`;
                labels[1] = 'Expected consumption (MRP + buffer)';
                values[1] = `${expectedDemand} (${futureOrders} + ${emergencyStock})`;
                labels[2] = 'Required amount (MRP * strategy)';
                values[2] = `${requiredQuantity} (${futureOrders} * ${logisticAmnt}%)`;
              }
            }

            const buffer = extraParams?.buffer;
            if (buffer && buffer > 0) {
              const { storage, prevPOs, prevFutureOrders, po, expectedDemand, futureOrder } = originalRow;
              const prevStg = Math.max(0, storage - prevPOs + prevFutureOrders);

              labels[0] = 'Storage ([previous] Storage + POs - MRP)';

              values[0] = `${Math.max(0, storage)} (${prevStg} + ${prevPOs} - ${prevFutureOrders})`;

              labels[1] = 'POs';
              values[1] = `${po}`;
              labels[2] = 'Expected Demand (MRP + Buffer)';
              values[2] = `${expectedDemand} (${futureOrder} + ${buffer})`;

              labels[3] = 'Buffer stock';
              values[3] = buffer.toString();
            }

            tooltipEl.innerHTML = renderCoverageTooltipHTML({
              values,
              labels,
              colors,
              lastNoColor: !!extraParams?.isCoverage,
            });
          }

          const position = context.chart.canvas.getBoundingClientRect();

          // Display, position, and set styles for font
          // Position tooltip at the bottom of the chart with fixed Y position
          tooltipEl.style.opacity = '1';
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
          tooltipEl.style.top = position.bottom + window.pageYOffset - 70 + 'px'; // Fixed at bottom of chart
        },
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked,
        grid: {
          display: true,
          color: '#F3F4F6',
          lineWidth: 1,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#6B7280',
        },
      },
      y: {
        stacked,
        beginAtZero: true,
        min: 0,
        grid: {
          color: (context) => {
            return context.tick.value === 0 ? '#374151' : '#F3F4F6';
          },
          lineWidth: 1,
        },
        ticks: {
          callback: (value) => shorten(+value),
          font: {
            size: 11,
          },
          color: '#6B7280',
        },
        title: {
          display: true,
          text: isQuantity ? unit?.displayName || 'mt' : 'USD $',
          color: '#374151',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      },
    },
  };

  const computedChartKey = useMemo(
    () =>
      `chart-${title}-${materialId}-${isQuantity ? 'quantity' : 'price'}-${matPrice}-${viewMonths}-${JSON.stringify(
        extraParams,
      )}`,
    [title, materialId, isQuantity, matPrice, viewMonths, extraParams],
  );

  const finalChartKey = externalChartKey || computedChartKey;

  if (materialId === GENERAL_ID) {
    return (
      <div className="flex flex-col bg-white shadow-md rounded-lg p-2 h-full">
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500 text-3xl">Select material</div>
        </div>
      </div>
    );
  }
  if (!data.length) {
    return (
      <div className="flex flex-col bg-white shadow-md rounded-lg p-2 h-full">
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white shadow-md rounded-lg p-2 h-full">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          <ToggleChips
            chips={valChips}
            selected={isQuantity ? 1 : 0}
            setSelected={(val: number) => setIsQuantity(val === 1)}
            width="5rem"
          />
          <ToggleChips chips={timeOpts} selected={timeGrans} setSelected={setTimeGrans} width="5rem" />
          <ToggleChips chips={viewRangeOpts} selected={viewMonths} setSelected={setViewMonths} width="5rem" />
        </div>
      </div>

      <div className="flex items-center justify-center mb-1">
        <button className="p-1 text-gray-500 hover:text-gray-700">
          <i className="fas fa-chevron-left" />
        </button>
        <span className="text-base font-medium mx-3">{new Date().getFullYear()}</span>
        <button className="p-1 text-gray-500 hover:text-gray-700">
          <i className="fas fa-chevron-right" />
        </button>
      </div>

      <div className="flex-1 overflow-hidden">
        <Chart key={finalChartKey} type="bar" data={chartData} options={options} plugins={plugins} />
      </div>
    </div>
  );
}
